import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, MenuItem, Modal, Select, Stack, TextField, Typography } from "@mui/material";
import { BarData, BarDataError, CountryData } from "../../types";
import { useEffect, useState } from "react";
import { useAPI } from "../../hooks/useAPI";
import { ConfirmDialog } from "../../components/global/ConfirmDialog";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";

interface BarModalProps {
  open: boolean;
  handleClose: () => void;
  handleSave: () => void;
  bar: BarData;
  countries: CountryData[];
}

export const BarModal = ({ open, handleClose, handleSave, bar, countries }: BarModalProps) => {
  const [mBar, setMBar] = useState<BarData>(bar);
  const [error, setError] = useState<BarDataError>({});
  const { fetchData } = useAPI();
  const { isOpen, config, openDialog, closeDialog } = useConfirmDialog();

  useEffect(() => {
    setMBar({ ...bar});
  }, [bar]);

  const onSaveBar = async (bar: BarData) => {
    const {errors, data} = await fetchData('/bars/save',  bar, 'POST');
    if (errors) {
      setError(errors);
    } else {
      handleSave();
    }
  }

  const onDeleteBar = async (bar: BarData) => {
    const {errors, data} = await fetchData('/bars/delete',  { id: bar.id }, 'POST');
    if (errors) {
      setError(errors);
    } else {
      handleSave();
    }
  }

  return (
    <Modal
      open={open}
      // onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={{ 
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        maxWidth: '750px',
        bgcolor: 'background.paper',
        border: '1px solid #909894',
        boxShadow: '0px 4px 4px 0px #00000040',
        borderRadius:'16px',
        p: 4,
        maxHeight: '90%',
        overflow: 'auto',
       }}>
        <Typography id="modal-title" variant="h4" component="h2" fontWeight="600">
          { mBar.id ? "Edit Bar" : "Add Bar" }
        </Typography>
        <Stack mt={4} spacing={4}>
          <FormGroup>
            <Typography fontWeight="600">Bar Name</Typography>
            <TextField
              variant="outlined"
              value={mBar.name}
              error={error.name ? true : false}
              helperText={error?.name?.[0] ?? ''}
              onChange={(e) => setMBar({ ...mBar, name: e.target.value })}
              sx={{ width: '100%' }}
            />
          </FormGroup>
          <FormGroup>
            <Typography fontWeight="600">Bar Address</Typography>
            <TextField
              variant="outlined"
              value={mBar.address}
              error={error.address ? true : false}
              helperText={error?.address?.[0] ?? ''}
              onChange={(e) => setMBar({ ...mBar, address: e.target.value })}
              sx={{ width: '100%' }}
            />
          </FormGroup>
          <FormGroup>
            <FormControl error={error.country_id ? true : false}>
              <Typography fontWeight="600">Country</Typography>
              <Select sx={{ width: '100%' }} value={mBar.country_id ?? ''} onChange={(e) => setMBar({ ...mBar, country_id: e.target.value })}>
                <MenuItem value="">&nbsp;</MenuItem>
                {
                  countries.map((item: CountryData, index: number) => (
                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                  ))
                }
              </Select>
              {
                error.country_id ? (
                  <FormHelperText color="#ff1744" style={{ marginBottom: "-20px" }}>
                    {error?.country_id?.[0] ?? ''}
                  </FormHelperText>
                ) : null
              }
            </FormControl>
          </FormGroup>
          <Stack spacing={2} direction="row">
            <Stack width="50%">
              <FormGroup>
                <Typography fontWeight="600">Postcode</Typography>
                <TextField
                  variant="outlined"
                  value={mBar.zip_code}
                  error={error.zip_code ? true : false}
                  helperText={error?.zip_code?.[0] ?? ''}
                  onChange={(e) => setMBar({ ...mBar, zip_code: e.target.value })}
                  sx={{ width: '100%' }}
                />
              </FormGroup>
            </Stack>
            <Stack width="50%">
              <FormGroup>
                <Typography fontWeight="600">&nbsp;</Typography>
                <FormControlLabel
                  control={<Checkbox/>}
                  style={{
                    marginTop: '12px', 
                    marginBottom: '8px', 
                    color: '#4B5851', 
                    fontStyle: 'italic',
                  }}
                  label="Bar Active"
                  checked={mBar.status ? true : false}
                  onChange={(e, status) => setMBar({ ...mBar, status: status })}
                />
              </FormGroup>
            </Stack>
          </Stack>
          <Stack spacing={2} direction="row">
            <Stack width="50%">
              <FormGroup>
                <Typography fontWeight="600">Logitude</Typography>
                <TextField
                  variant="outlined"
                  value={mBar.logitude}
                  error={error.logitude ? true : false}
                  helperText={error?.logitude?.[0] ?? ''}
                  onChange={(e) => setMBar({ ...mBar, logitude: e.target.value })}
                  sx={{ width: '100%' }}
                />
              </FormGroup>
            </Stack>
            <Stack width="50%">
            <FormGroup>
                <Typography fontWeight="600">Latitude</Typography>
                <TextField
                  variant="outlined"
                  value={mBar.latitude}
                  error={error.latitude ? true : false}
                  helperText={error?.latitude?.[0] ?? ''}
                  onChange={(e) => setMBar({ ...mBar, latitude: e.target.value })}
                  sx={{ width: '100%' }}
                />
              </FormGroup>
            </Stack>
          </Stack>
          <FormGroup>
            <Stack direction='row' spacing={2}>
              <Button variant="contained" size="large" onClick={() => onSaveBar(mBar)}>
                Save Bar
              </Button>
              {
                mBar.id ? (
                  <Button variant="contained" size="large" color="error" onClick={() => 
                    openDialog(
                      "Confirm Deletion",
                      `Are you sure you want to delete the client "${mBar.name}"?`,
                      () => onDeleteBar(mBar)
                    )
                  }>
                    Delete Bar
                  </Button>
                ) : null
              }
              <Button variant="text" size="large" onClick={handleClose}>
                Cancel
              </Button>
            </Stack>
          </FormGroup>
        </Stack>
        <ConfirmDialog
          open={isOpen}
          title={config.title}
          description={config.description}
          onConfirm={config.onConfirm}
          onClose={closeDialog}
        />
      </Box>
    </Modal>
  )
}